import JSBI from 'jsbi';
import { Provider } from 'near-api-js/lib/providers';
import { CodeResult } from '../../types';
import { addDecimals } from '../../utils/math';
import {
  CommonRefFinancePool,
  PoolKind,
  RawRefFinancePool,
  RawRefFinanceRatedPool,
  RawRefFinanceSimplePool,
  RawRefFinanceStablePool,
  RefFinancePool,
  RefFinanceSimplePool,
  RefFinanceStablePool,
} from './types';
import {
  createCAmountReserves,
  createCorrelation,
  createReserves,
} from './utils';

const getNumberOfPools = async ({
  provider,
  ammId,
}: {
  provider: Provider;
  ammId: string;
}): Promise<number> => {
  const poolsNumber: number = await provider
    .query<CodeResult>({
      request_type: 'call_function',
      account_id: ammId,
      method_name: 'get_number_of_pools',
      args_base64: Buffer.from(JSON.stringify({})).toString('base64'),
      finality: 'optimistic',
    })
    .then((res) => JSON.parse(Buffer.from(res.result).toString()));

  return poolsNumber;
};

const parseSimplePool = (
  pool: RawRefFinancePool,
  id: number
): CommonRefFinancePool => {
  return {
    ...pool,
    id,
    reserves: createReserves(pool),
  };
};

const loadSimplePool = async ({
  provider,
  ammId,
  poolId,
}: {
  provider: Provider;
  ammId: string;
  poolId: number;
}): Promise<CommonRefFinancePool> => {
  const pool = await provider
    .query<CodeResult>({
      request_type: 'call_function',
      account_id: ammId,
      method_name: 'get_pool',
      args_base64: Buffer.from(JSON.stringify({ pool_id: poolId })).toString(
        'base64'
      ),
      finality: 'optimistic',
    })
    .then<RawRefFinanceSimplePool>((res) =>
      JSON.parse(Buffer.from(res.result).toString())
    );

  const parsedPool = parseSimplePool(pool, poolId);

  return parsedPool;
};

const loadPool = async ({
  provider,
  ammId,
  poolId,
  poolKind,
}: {
  provider: Provider;
  ammId: string;
  poolId: number;
  poolKind?: PoolKind;
}): Promise<RefFinancePool> => {
  if (poolKind) {
    switch (poolKind) {
      case 'STABLE_SWAP':
        return await getStablePool(provider, ammId, poolId);
      case 'RATED_SWAP':
        return await getRatedPool(provider, ammId, poolId);
      default:
        // @ts-ignore
        return await loadSimplePool({ provider, ammId, poolId });
    }
  } else {
    // @ts-ignore
    return await loadSimplePool({ provider, ammId, poolId });
  }
};

const loadPools = async ({
  provider,
  ammId,
  index = 0,
  limit,
}: {
  provider: Provider;
  ammId: string;
  index: number;
  limit?: number;
}): Promise<RawRefFinancePool[]> => {
  const pools = await provider
    .query<CodeResult>({
      request_type: 'call_function',
      account_id: ammId,
      method_name: 'get_pools',
      args_base64: Buffer.from(
        JSON.stringify({ from_index: index, limit })
      ).toString('base64'),
      finality: 'optimistic',
    })
    .then<RawRefFinancePool[]>((res) =>
      JSON.parse(Buffer.from(res.result).toString())
    );

  return pools;
};

const parseStableSwapPool = (
  pool: RawRefFinanceStablePool,
  id: number
): RefFinanceStablePool => {
  const STABLE_SWAP_LP_DECIMALS = 18;

  return {
    ...pool,
    id,
    reserves: createReserves(pool),
    cAmountReserves: createCAmountReserves(pool),
    decimals: createCorrelation(pool.token_account_ids, pool.decimals),
    // @ts-ignore
    rates: createCorrelation(
      pool.token_account_ids,
      pool.c_amounts.map(() =>
        addDecimals(JSBI.BigInt(1), STABLE_SWAP_LP_DECIMALS)
      )
    ),
    pool_kind: 'STABLE_SWAP',
  };
};

export async function getStablePool(
  provider: Provider,
  exchange: string,
  poolId: number
): Promise<RefFinanceStablePool> {
  const pool = await provider
    .query<CodeResult>({
      request_type: 'call_function',
      account_id: exchange,
      method_name: 'get_stable_pool',
      args_base64: Buffer.from(JSON.stringify({ pool_id: poolId })).toString(
        'base64'
      ),
      finality: 'optimistic',
    })
    .then<RawRefFinanceStablePool>((res) =>
      JSON.parse(Buffer.from(res.result).toString())
    );

  return parseStableSwapPool(pool, poolId);
}

const parseRatedPool = (
  pool: RawRefFinanceRatedPool,
  poolId: number
): RefFinanceStablePool => {
  return {
    ...pool,
    id: poolId,
    reserves: createReserves(pool),
    cAmountReserves: createCAmountReserves(pool),
    decimals: createCorrelation(pool.token_account_ids, pool.decimals),
    rates: createCorrelation(
      pool.token_account_ids,
      pool.rates.map((r: string) => JSBI.BigInt(r))
    ),
    pool_kind: 'RATED_SWAP',
  };
};

/**
 * Fetch a rated pool. Rated pools are an improved version of stable pools.
 * @param provider
 * @param poolId
 * @returns
 */
export async function getRatedPool(
  provider: Provider,
  exchange: string,
  poolId: number
): Promise<RefFinanceStablePool> {
  const pool = await provider
    .query<CodeResult>({
      request_type: 'call_function',
      account_id: exchange,
      method_name: 'get_rated_pool',
      args_base64: Buffer.from(JSON.stringify({ pool_id: poolId })).toString(
        'base64'
      ),
      finality: 'optimistic',
    })
    .then<RawRefFinanceRatedPool>((res) =>
      JSON.parse(Buffer.from(res.result).toString())
    );

  return parseRatedPool(pool, poolId);
}

const loadAllPools = async ({
  provider,
  ammId,
}: {
  provider: Provider;
  ammId: string;
}) => {
  const numberOfPools = await getNumberOfPools({
    provider,
    ammId,
  });

  let poolsCounter = 0;
  let poolsPromises = [];

  const poolsBatchToLoad = 500;

  while (numberOfPools > poolsCounter) {
    const poolsBatchPromise = loadPools({
      provider,
      ammId,
      index: poolsCounter,
      limit: poolsBatchToLoad,
    });

    poolsPromises.push(poolsBatchPromise);
    poolsCounter += poolsBatchToLoad;
  }

  const pools = (await Promise.all(poolsPromises)).flat();
  const poolsWithIndexes = pools.map((pool, index) =>
    parseSimplePool(pool, index)
  );

  // @ts-ignore
  const simplePools: RefFinanceSimplePool[] = poolsWithIndexes.filter(
    (pool) => pool.pool_kind === 'SIMPLE_POOL'
  );

  const rawStablePools = poolsWithIndexes.filter(
    (pool) => pool.pool_kind !== 'SIMPLE_POOL'
  );

  const stablePools: RefFinanceStablePool[] = await Promise.all(
    rawStablePools.map((pool) => {
      if (pool.pool_kind === 'STABLE_SWAP') {
        return getStablePool(provider, ammId, pool.id);
      } else {
        return getRatedPool(provider, ammId, pool.id);
      }
    })
  );

  return { simplePools, stablePools };
};

// will test later
const loadAccountBalance = async ({
  provider,
  token,
  accountId,
}: {
  provider: Provider;
  token: string;
  accountId: string;
}) => {
  const balance = await provider
    .query<CodeResult>({
      request_type: 'call_function',
      account_id: token,
      method_name: 'storage_balance_of',
      args_base64: Buffer.from(
        JSON.stringify({ account_id: accountId })
      ).toString('base64'),
      finality: 'optimistic',
    })
    .then((res) => JSON.parse(Buffer.from(res.result).toString()));

  return balance;
};

export {
  getNumberOfPools,
  loadPool,
  loadPools,
  loadAllPools,
  loadAccountBalance,
};
